import { API_BASE_URL } from "./config";
import { deadlineSignal } from "./deadline";
import { toApiError } from "./errors";

/** Token pair returned by login/refresh (mirrors the OpenAPI AuthTokenPair). */
export interface AuthTokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

const AUTH_REQUEST_TIMEOUT_MS = 20_000;

// Auth endpoints use raw fetch so they never carry a stale Authorization
// header or an Idempotency-Key. Server-side rate limit: 5 rps/IP, burst 10.
async function postAuth<T>(path: string, body: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  let response: Response;
  try {
    // Bounded: a refresh holds the cross-tab lock while it runs, so a stalled
    // request must not block every tab's refresh and sign-out indefinitely.
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...extraHeaders },
      body: JSON.stringify(body),
      signal: deadlineSignal(AUTH_REQUEST_TIMEOUT_MS),
    });
  } catch {
    throw toApiError(undefined, undefined);
  }
  if (!response.ok) {
    const errorBody = await response.json().catch(() => undefined);
    throw toApiError(errorBody, response);
  }
  if (response.status === 204) return undefined as T;
  // A body cut off (deadline, dropped connection) or not JSON: an ApiError
  // like any failed call, never a raw SyntaxError/AbortError.
  try {
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  } catch {
    throw toApiError(undefined, undefined);
  }
}

export interface LoginInput {
  email: string;
  password: string;
  /** Cloudflare Turnstile token; forwarded for future backend verification. */
  turnstileToken?: string;
}

export function login(input: LoginInput): Promise<AuthTokenPair> {
  const headers = input.turnstileToken ? { "cf-turnstile-response": input.turnstileToken } : undefined;
  return postAuth<AuthTokenPair>(
    "/api/v1/auth/login",
    { email: input.email, password: input.password },
    headers,
  );
}

export function refresh(refreshToken: string): Promise<AuthTokenPair> {
  return postAuth<AuthTokenPair>("/api/v1/auth/refresh", { refresh_token: refreshToken });
}

export function logout(refreshToken: string): Promise<void> {
  return postAuth<void>("/api/v1/auth/logout", { refresh_token: refreshToken });
}
